// src/pages/MyTeam.jsx — Dark Theme My Team Page
import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import { Users, Lock, Unlock } from "lucide-react";
import "./TeamPortfolio.css";

const MyTeam = () => {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.role) { setLoading(false); return; }
    setLoading(true);
    Promise.all([api.get("/employees"), api.get("/leads?limit=5000")])
      .then(([empRes, leadRes]) => {
        setEmployees(Array.isArray(empRes.data) ? empRes.data : []);
        setLeads(Array.isArray(leadRes.data) ? leadRes.data : []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user?.role]);

  const teamAgents = useMemo(() => {
    if (!user?.role) return [];
    const agents = employees.filter((e) => e?.role === "Agent");
    if (user.role === "Admin") return agents;
    if (user.role === "Manager") {
      const myAgents = agents.filter((a) => (a.managerId === user.id || a.managerName === user.name));
      const myself = employees.find(e => e.id === user.id);
      return myself ? [myself, ...myAgents] : myAgents;
    }
    return [];
  }, [employees, user]);

  const countByAgent = useMemo(() => {
    const byId = {}, byName = {};
    leads.filter((l) => l.status === "Customer").forEach((l) => {
      if (l.agentId) byId[String(l.agentId)] = (byId[String(l.agentId)] || 0) + 1;
      else if (l.agentName) byName[l.agentName] = (byName[l.agentName] || 0) + 1;
    });
    return { byId, byName };
  }, [leads]);

  const totalCustomers = teamAgents.reduce((sum, a) => {
    const id = String(a._id || a.id || "");
    const name = a.name || "";
    return sum + (countByAgent.byId[id] || countByAgent.byName[name] || 0);
  }, 0);

  const handleToggleLock = async (id, currentStatus) => {
    try {
      const { data } = await api.put(`/employees/lock/${id}`, { isLocked: !currentStatus });
      setEmployees((prev) => prev.map((e) => (String(e.id) === String(id) ? { ...e, isLocked: data.isLocked } : e)));
      toast.success(data.isLocked ? "Account Locked" : "Account Unlocked");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to toggle lock");
    }
  };

  return (
    <div className="tp-page">
      {/* ── Page Toolbar ── */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <span className="badge rounded-pill" style={{ background: 'var(--primary-light)', color: 'var(--primary)', border: '1px solid var(--primary-border)', padding: '6px 12px', fontSize: '12px', fontWeight: 600 }}>
          {teamAgents.length} Team Members
        </span>
        <span className="text-muted small d-none d-md-inline">
          {user?.role === "Admin" ? "All company sales representatives" : "Agents reporting directly to your team"}
        </span>
      </div>

      {/* ── Stats Row ── */}
      <div className="tp-stats-grid">
        <div className="tp-stat-card">
          <div className="tp-stat-card__value">{teamAgents.length}</div>
          <div className="tp-stat-card__label">Team Members</div>
        </div>
        <div className="tp-stat-card">
          <div className="tp-stat-card__value" style={{ color: '#22c55e' }}>{totalCustomers}</div>
          <div className="tp-stat-card__label">Total Converted Customers</div>
        </div>
      </div>

      {/* ── Table Card ── */}
      <div className="tp-card">
        <div className="tp-card__head">
          <span className="tp-card__title">
            <Users size={16} /> Team Members List
          </span>
        </div>

        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Loading team members...</div>
        ) : teamAgents.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No team members assigned.</div>
        ) : (
          <table className="tp-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Online Status</th>
                {user?.role === "Admin" && <th>Manager</th>}
                <th>Customers</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {teamAgents.map((emp) => {
                const id = String(emp._id || emp.id || "");
                const customers = countByAgent.byId[id] || countByAgent.byName[emp.name] || 0;
                let resolvedStatus = emp.onlineStatus || 'offline';

                return (
                  <tr key={id || emp.email}>
                    <td style={{ fontWeight: 600, color: '#f8fafc' }}>
                      {emp.name || "—"} {emp.id === user?.id && <span style={{ color: '#38bdf8', fontSize: '11px' }}>(You)</span>}
                    </td>
                    <td style={{ color: '#cbd5e1' }}>{emp.email || "—"}</td>
                    <td>
                      <span className="cell-badge" style={{ background: '#334155', color: '#f8fafc' }}>{emp.role}</span>
                    </td>
                    <td>
                      <span className={`staleness-dot ${resolvedStatus === 'online' ? 'green' : 'amber'}`} style={{ marginRight: '6px' }} />
                      <span style={{ textTransform: 'capitalize' }}>{resolvedStatus}</span>
                    </td>
                    {user?.role === "Admin" && <td>{emp.managerName || "—"}</td>}
                    <td style={{ fontWeight: 700, color: '#22c55e' }}>{customers}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className="quick-act-btn"
                        style={{ width: 'auto', padding: '4px 10px' }}
                        onClick={() => handleToggleLock(id, emp.isLocked)}
                      >
                        {emp.isLocked ? <Unlock size={12} /> : <Lock size={12} />}
                        <span style={{ marginLeft: '4px' }}>{emp.isLocked ? "Unlock" : "Lock"}</span>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default MyTeam;